import type { Json } from '@auctisync/shared';

import type { AuctisyncSupabase } from './client.js';

/**
 * App diagnostic reports — sent by the Cataloging app, read on the Super
 * Tenant Dash (Jim, 2026-10-05: "Route the reports to the Super Tenant Dash").
 *
 * A report is what the app saw when something went wrong: why it was made, the
 * screen as iOS saw it, the app's recent events (names and numbers only), the
 * device. The app builds it (`apps/cataloging/src/diagnostics`); the server
 * (`public.record_app_diagnostic_report`) takes it from staff only, size-capped,
 * 100 per person per day, once per report id; super admins alone read it
 * (`list_app_diagnostic_reports`, `get_app_diagnostic_report`). Kept 90 days.
 */

export type DiagnosticTrigger = 'touches_lost' | 'interaction_off' | 'js_error' | 'manual';

export const DIAGNOSTIC_TRIGGERS: readonly DiagnosticTrigger[] = [
  'touches_lost',
  'interaction_off',
  'js_error',
  'manual',
];

/** How the dash names each reason a report was made. */
export const DIAGNOSTIC_TRIGGER_LABEL: Record<DiagnosticTrigger, string> = {
  touches_lost: 'Screen stopped taking touches',
  interaction_off: 'Touches switched off',
  js_error: 'App error',
  manual: 'Reported by a worker',
};

export type SendReportResult = { ok: true } | { ok: false; retry: boolean; message: string };

/**
 * Send one report. `retry: false` means the server will never take it — it is
 * not a report, or the account is not house staff — so the tablet stops trying;
 * anything else (offline, signed out, a server hiccup) is worth another go.
 */
export async function sendDiagnosticReport(
  supabase: AuctisyncSupabase,
  report: object,
  tenantId: string | null,
): Promise<SendReportResult> {
  const { error } = await supabase.rpc('record_app_diagnostic_report', {
    p_report: report as Json,
    ...(tenantId ? { p_tenant_id: tenantId } : {}),
  });
  if (!error) return { ok: true };
  const message = error.message ?? 'Unknown error';
  const code = (error as { code?: string }).code ?? '';
  const permanent = code === '22023' || /sent by house staff/i.test(message);
  return { ok: false, retry: !permanent, message };
}

export interface DiagnosticReportRow {
  id: string;
  received_at: string;
  reported_at: string;
  tenant_id: string | null;
  tenant_name: string | null;
  user_id: string | null;
  user_email: string | null;
  app: string;
  trigger: DiagnosticTrigger;
  platform: string | null;
  os_version: string | null;
  device_model: string | null;
  app_version: string | null;
  app_build: string | null;
  lot_id: string | null;
  auction_id: string | null;
  recovered: boolean | null;
  note: string | null;
  payload_bytes: number;
}

export interface DiagnosticReportFilter {
  /** How far back, in days (1–365). */
  days?: number;
  tenantId?: string | null;
  trigger?: DiagnosticTrigger | null;
}

/** The list on the dash, newest first (at most 500). Super admins only — anyone else gets an error, never an empty list. */
export async function fetchDiagnosticReports(
  supabase: AuctisyncSupabase,
  filter: DiagnosticReportFilter = {},
): Promise<{ rows: DiagnosticReportRow[]; error?: { message: string } }> {
  const { data, error } = await supabase.rpc('list_app_diagnostic_reports', {
    p_days: filter.days ?? 30,
    ...(filter.tenantId ? { p_tenant_id: filter.tenantId } : {}),
    ...(filter.trigger ? { p_trigger: filter.trigger } : {}),
  });
  if (error) return { rows: [], error: { message: error.message } };
  return { rows: (data ?? []) as unknown as DiagnosticReportRow[] };
}

/** One report in full. Null when there is no such report (or it has been purged). */
export async function fetchDiagnosticReport(
  supabase: AuctisyncSupabase,
  id: string,
): Promise<{ report: Record<string, unknown> | null; error?: { message: string } }> {
  const { data, error } = await supabase.rpc('get_app_diagnostic_report', { p_id: id });
  if (error) return { report: null, error: { message: error.message } };
  return {
    report:
      data && typeof data === 'object' && !Array.isArray(data)
        ? (data as Record<string, unknown>)
        : null,
  };
}
